import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, List, LogOut } from 'lucide-react';
import { Client, ResponseType } from '@microsoft/microsoft-graph-client';
import { PublicClientApplication } from '@azure/msal-browser';
import logoCEU from '../../../../assets/images/CEU_Chat_blanco.png';
import { configuration } from '../../../../constants/auth';

interface SMNavbarProps {
  userName: string | undefined;
  userEmail: string;
  userToken?: string;
  onLogout: () => void;
}

const msalInstance = new PublicClientApplication(configuration);

const initializeMsal = async () => {
  await msalInstance.initialize();
};

const SMNavbar: React.FC<SMNavbarProps> = ({ userName, userEmail, userToken, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);

  const canViewFullRegistry = useMemo(() => {
    try {
      const raw = process.env.REACT_APP_SM_ACCESAD_LIST;
      if (!raw) return false;
      const list = JSON.parse(raw);
      if (Array.isArray(list)) {
        return list.map((e: string) => e.toLowerCase().trim()).includes(userEmail.toLowerCase().trim());
      }
    } catch {
      return false;
    }
    return false;
  }, [userEmail]);

  const navLinks = [
    { to: '/gestion-servicios', label: 'Inicio', icon: <Home size={16} /> },
    ...(canViewFullRegistry ? [{ to: '/gestion-servicios/lista', label: 'Registro servicios', icon: <List size={16} /> }] : []),
  ];

  useEffect(() => {
    if (!userToken) return;
    let currentPhotoUrl: string | null = null;

    const getProfilePhoto = async () => {
      try {
        await initializeMsal();
        const account = msalInstance.getAllAccounts()[0];
        if (!account) return;

        const tokenResponse = await msalInstance.acquireTokenSilent({
          scopes: ['User.Read'],
          account,
        });

        const client = Client.init({
          authProvider: (done) => done(null, tokenResponse.accessToken),
        });

        const photoResponse = await client
          .api('/me/photo/$value')
          .responseType(ResponseType.BLOB)
          .get();

        if (!photoResponse) {
          throw new Error('Photo not found');
        }

        const url = URL.createObjectURL(photoResponse);
        currentPhotoUrl = url;
        setProfilePhoto(url);
      } catch {
        setProfilePhoto(null);
      }
    };

    getProfilePhoto();

    return () => {
      if (currentPhotoUrl) {
        URL.revokeObjectURL(currentPhotoUrl);
      }
    };
  }, [userToken]);

  return (
    <nav className="sm-navbar">
      <div 
        className="sm-navbar-brand" 
        onClick={() => navigate('/')} 
        style={{ cursor: 'pointer' }}
        title="Volver al chat"
      >
        <img
          src={logoCEU}
          alt="GPT-CEU"
          className="sm-navbar-logo"
        />
      </div>

      <div className="sm-navbar-links">

        {navLinks.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className={`sm-navbar-link ${location.pathname === link.to ? 'active' : ''}`}
          >
            {link.icon}
            {link.label}
          </Link>
        ))}
      </div>

      <div className="sm-navbar-user">
        {profilePhoto ? (
          <img src={profilePhoto} alt="Profile" className="sm-user-avatar sm-user-avatar-image" />
        ) : (
          <div className="sm-user-avatar">
            {userName?.charAt(0).toUpperCase()}
          </div>
        )}
        <span className="sm-user-name">{userName}</span>
        <button className="sm-logout-btn" onClick={onLogout} title="Cerrar sesión">
          <LogOut size={16} />
        </button>
      </div>
    </nav>
  );
};

export default SMNavbar;
